var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert, showAlert as showAlertAction, showNotification, } from '../../../actions/NotificationActions';
import { apiUpdateProfile } from '../../../actions/ProfileActions';
import { PROFESSIONAL_POSITION_CANDIDATES } from '../../../define/consts/profile';
import { wrapDispatch } from '../../../helpers/action';
import { isCanUseProfessionalProffit, isCanUseProfessionalProffitMart, } from '../../../helpers/auth';
import { isNullOrUndefinedOrEmpty } from '../../../helpers/common';
import { toDotArray } from '../../../helpers/common/ArrayHelper';
import { uploadFilePromiseAndGetTemporaryId } from '../../../helpers/files';
import { formValidateScroll } from '../../../helpers/form';
import { getSolutionUrlFromId } from '../../../helpers/profile';
import { hasRequiredParam } from '../../../helpers/user/UserProfileHelper';
import Layout from '../../Layout';
import AuthenticationHoc from '../../System/AuthenticationHoc';
import FormConsultantProfileSticky from '../../Ui/Professional/Profile/FormConsultantProfileSticky';
import FormProfileEdit from './FormProfileEdit';
const ProfileEdit = () => {
    const dispatch = useDispatch();
    const { auth, profile } = useSelector((state) => ({
        auth: state.auth,
        profile: state.profile,
    }));
    const formName = 'FormProfileEdit';
    const initialValues = useMemo(() => {
        const { information = {} } = profile;
        const { baseSkills, careers, experienceClasses, experiencedIndustryClasses, middleTasks, personalities, position, prefecture, projectCareers, relatedDocuments, relatedLinks, relatedSolutions, sex, situation, } = information;
        const positionIdList = PROFESSIONAL_POSITION_CANDIDATES.map((v) => v.value);
        let initialPosition = position;
        const initialPositionNumber = initialPosition
            ? parseInt(initialPosition, 10)
            : 0;
        if (!positionIdList.includes(initialPositionNumber)) {
            initialPosition = '';
        }
        // キャリア
        const careerList = careers || [];
        if (careerList.length === 0) {
            careerList.push({ date: new Date() });
        }
        const initialCareers = (careerList || []).map((row) => {
            const { date } = row, addRow = __rest(row, ["date"]);
            const momentDate = moment(date);
            return Object.assign({ month: momentDate.format('M'), year: momentDate.format('YYYY') }, addRow);
        });
        // 関連プロジェクト事例
        const solutions = (relatedSolutions || []).map((value) => (Object.assign(Object.assign({}, value), { url: getSolutionUrlFromId(value.id) })));
        // デフォルト1件を追加する
        const addDefaultRow = (rows) => {
            const checkRows = rows || [];
            if (checkRows.length === 0) {
                checkRows.push({});
            }
            return checkRows;
        };
        const convertCheckRows = (rows) => {
            const pairs = (rows || []).map((row) => [
                row.id,
                Object.assign(Object.assign({}, row), { check: true }),
            ]);
            return _.fromPairs(pairs);
        };
        return Object.assign(Object.assign({}, profile), { information: Object.assign(Object.assign({}, profile.information), { baseSkills: convertCheckRows(baseSkills), careers: initialCareers, experienceClasses: convertCheckRows(experienceClasses), experiencedIndustryClasses: convertCheckRows(experiencedIndustryClasses), middleTasks: convertCheckRows(middleTasks), personalities: convertCheckRows(personalities), position: initialPosition.toString(), prefecture: prefecture ? prefecture.toString() : '1', projectCareers: addDefaultRow((projectCareers || []).map((projectCareer) => (Object.assign(Object.assign({}, projectCareer), { 
                    // select2向けの変換
                    middleTasks: (projectCareer.middleTasks || []).map((middleTask) => ({
                        label: middleTask.name,
                        value: middleTask.id,
                    })) })))), relatedDocuments: addDefaultRow(relatedDocuments), relatedLinks: addDefaultRow(relatedLinks), relatedSolutions: addDefaultRow(solutions), sex: sex ? sex.toString() : '1', situation: situation ? situation.toString() : '1' }), organizationId: profile.organization ? profile.organization.id : '' });
    }, [profile]);
    const useProffit = isCanUseProfessionalProffit(profile);
    const useProffitMart = isCanUseProfessionalProffitMart(profile);
    const setOnSubmitCompleteCallbackRef = useRef();
    const setOnSubmitStartCallbackRef = useRef();
    useEffect(() => {
        if (!hasRequiredParam(auth, profile)) {
            dispatch(showAlertAction('プロフィール情報が不十分です。必須項目を入力してください。'));
        }
    }, [auth, profile, dispatch]);
    const onSubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setOnSubmitStartCallbackRef.current();
            const newValues = Object.assign(Object.assign({}, _.pick(values, [
                'password',
                'organizationId',
                'otherOrganization',
            ])), _.pick(values.information, [
                'lastName',
                'firstName',
                'lastNameKana',
                'firstNameKana',
                'phone1',
                'phone2',
                'phone3',
                'department',
                'position',
                'positionOther',
                'zipCode1',
                'zipCode2',
                'prefecture',
                'address1',
                'address2',
                'address3',
                'sex',
                'situation',
                'experience',
                'description',
                'catchphrase',
                'cardPhoto',
                'file',
                'baseSkills',
                'experiencedIndustryClasses',
                'experienceClasses',
                'middleTasks',
                'personalities',
                'careers',
                'projectCareers',
                'relatedLinks',
                'relatedDocuments',
                'relatedSolutions',
            ]));
            // 各データの整形
            // checkbox系
            const getFilteredList = (list) => Object.keys(list)
                .map((id) => (Object.assign({ id }, list[id])))
                .filter((v) => v.check);
            const fillEspecially = (list) => getFilteredList(list).map((v) => (Object.assign(Object.assign({}, v), { especially: v.especially ? 1 : 0 })));
            const checkFilters = {
                baseSkills: getFilteredList,
                experienceClasses: getFilteredList,
                experiencedIndustryClasses: fillEspecially,
                middleTasks: fillEspecially,
                personalities: getFilteredList,
            };
            Object.keys(checkFilters).forEach((name) => {
                newValues[name] = checkFilters[name](newValues[name]);
            });
            // キャリア
            newValues.careers = newValues.careers
                .filter((v) => !isNullOrUndefinedOrEmpty(v.title) &&
                !isNullOrUndefinedOrEmpty(v.content))
                .map((v) => (Object.assign({ date: `${v.year}-${v.month}-1` }, v)));
            newValues.projectCareers = newValues.projectCareers
                .filter((v) => !isNullOrUndefinedOrEmpty(v.title) &&
                !isNullOrUndefinedOrEmpty(v.content) &&
                !isNullOrUndefinedOrEmpty(v.middleTasks) &&
                Array.isArray(v.middleTasks) &&
                v.middleTasks.length > 0)
                .map((projectCareer) => (Object.assign(Object.assign({}, projectCareer), { middleTasks: projectCareer.middleTasks.map((middleTask) => ({
                    id: middleTask.value,
                })) })));
            newValues.relatedLinks = newValues.relatedLinks.filter((v) => !isNullOrUndefinedOrEmpty(v.title) &&
                !isNullOrUndefinedOrEmpty(v.linkurl));
            newValues.relatedDocuments = newValues.relatedDocuments.filter((v) => !isNullOrUndefinedOrEmpty(v.title) &&
                (!isNullOrUndefinedOrEmpty(v.id) ||
                    !isNullOrUndefinedOrEmpty(v.fileId) ||
                    !isNullOrUndefinedOrEmpty(v.uploadRawFile)));
            const uploadFileFunc = (obj, fileInputName, setName) => __awaiter(void 0, void 0, void 0, function* () {
                let result = null;
                const targetFile = obj[fileInputName];
                if (targetFile && targetFile instanceof File) {
                    result = yield uploadFilePromiseAndGetTemporaryId({
                        dispatch,
                        file: targetFile,
                    });
                }
                _.set(obj, setName, result);
                if (fileInputName !== setName) {
                    _.set(obj, fileInputName, undefined);
                }
            });
            newValues.relatedSolutions = newValues.relatedSolutions.filter((v) => !isNullOrUndefinedOrEmpty(v.id));
            // ファイルの変換
            for (let i = 0; i < newValues.relatedDocuments.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                yield uploadFileFunc(newValues.relatedDocuments[i], 'uploadRawFile', 'fileId');
            }
            yield uploadFileFunc(newValues, 'file', 'fileId');
            yield uploadFileFunc(newValues, 'cardPhoto', 'cardPhoto');
            yield wrapDispatch(dispatch, apiUpdateProfile(newValues));
            dispatch(showNotification('プロフィールを更新しました。'));
            setOnSubmitCompleteCallbackRef.current(true);
        }
        catch (e) {
            setOnSubmitCompleteCallbackRef.current(false);
            dispatch(showAlert('システムエラーが発生しました。しばらく時間を置いてからやり直すか、管理者にお問い合わせください。'));
            throw e;
        }
    }), [dispatch, setOnSubmitCompleteCallbackRef]);
    const onSubmitFail = useCallback((errors) => {
        const dots = Object.keys(toDotArray(errors || {}, '', {}, true));
        formValidateScroll(dots[dots.length - 1]);
    }, []);
    return (_jsxs(Layout, Object.assign({ mainClassName: 'l-main professional_profile', sticky: _jsx(FormConsultantProfileSticky, { formName: formName, previewUrl: `/consultant/${profile.id}`, setOnSubmitCompleteCallbackRef: setOnSubmitCompleteCallbackRef, setOnSubmitStartCallbackRef: setOnSubmitStartCallbackRef }) }, { children: [_jsx("header", Object.assign({ className: 'l-sub-header l-sub-header-profile l-container-12' }, { children: _jsx("h1", { children: "Profile" }) })), _jsx("div", Object.assign({ className: 'l-container l-contents-form l-container-9 md-l-container-full' }, { children: _jsx(FormProfileEdit, { enableReinitialize: true, form: formName, formName: formName, initialValues: initialValues, onSubmit: onSubmit, onSubmitFail: onSubmitFail, useProffit: useProffit, useProffitMart: useProffitMart }) }))] })));
};
export default AuthenticationHoc({ isProfessional: true })(ProfileEdit);
